import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const Stars = ({ n }) => (
  <span className="inline-flex text-terracotta">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} fill={i <= Math.round(n) ? "currentColor" : "none"} strokeWidth={1.5} />)}</span>
);

const CLAMP_CHARS = 280;

function ClampText({ text, testid }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  if (!text) return null;
  const long = text.length > CLAMP_CHARS;
  const shown = open || !long ? text : text.slice(0, CLAMP_CHARS).trimEnd() + "…";
  return (
    <>
      {shown}
      {long && (
        <button
          data-testid={testid}
          onClick={() => setOpen((v) => !v)}
          className="ml-1 text-terracotta underline underline-offset-2 font-medium"
        >
          {open ? (lang === "es" ? "Ver menos" : "See less") : (lang === "es" ? "Ver más" : "See more")}
        </button>
      )}
    </>
  );
}

export function ReviewCard({ r }) {
  const month = r.created_at ? new Date(r.created_at).toLocaleString(undefined, { month: "short", year: "numeric" }) : "";
  const route = [r.city_from, r.city_to].filter(Boolean).join(" → ");
  return (
    <figure data-testid={`review-card-${r.id}`} className="rounded-3xl border border-sand-300 bg-white p-6 flex flex-col">
      <Stars n={r.rating} />
      <blockquote className="mt-3 flex-1 text-charcoal-700 leading-relaxed"><ClampText text={r.trip_text} testid={`review-seemore-${r.id}`} /></blockquote>
      {r.pet_photo && <img src={r.pet_photo} alt="" className="mt-3 rounded-xl h-40 w-full object-cover" />}
      {r.pet_story && <p className="mt-2 text-sm text-charcoal-600 italic"><ClampText text={r.pet_story} testid={`petstory-seemore-${r.id}`} /> <span className="not-italic">🐾</span><br /><span className="text-xs text-charcoal-400">Written by {r.display_name} for {r.pet_name}</span></p>}
      <figcaption className="mt-4 border-t border-sand-200 pt-3 text-xs text-charcoal-500">
        <span className="font-medium text-charcoal-700">{r.display_name}</span>
        <div className="mt-1 inline-flex items-center gap-2 bg-sage-50 text-sage-800 rounded-full px-2 py-0.5">Verified client · {month}{route ? ` · ${route}` : ""}</div>
        <span className="ml-2 text-[10px] uppercase tracking-wide text-charcoal-400">{r.language}</span>
      </figcaption>
      {r.business_reply && <div className="mt-3 text-sm bg-sand-50 rounded-xl p-3"><span className="font-medium text-charcoal-700">Hola Movers:</span> {r.business_reply}</div>}
    </figure>
  );
}

export default function ReviewsBlock() {
  const { lang, t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reviews").then((r) => setData(r.data)).catch(() => setData({ items: [], summary: { count: 0, average: 0 } }));
  }, []);
  if (!data) return null;
  const { summary } = data;
  // Hide the whole line when there are no verified reviews.
  if (!summary || summary.count === 0) return null;

  const scrollToTestimonials = () => {
    document.getElementById("testimonials")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="mx-auto max-w-7xl px-6 lg:px-12 py-10" data-testid="reviews-block">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800">{lang === "es" ? "Lo que dicen los clientes" : "What clients say"}</h2>
        <span className="inline-flex items-center gap-2 text-charcoal-700" data-testid="reviews-summary">
          <Stars n={summary.average} /><b>{summary.average}</b>
          <span className="text-sm text-charcoal-500">· {summary.count} {lang === "es" ? "verificada" : "verified"}{summary.count !== 1 && lang === "es" ? "s" : ""}</span>
        </span>
        <button
          type="button"
          onClick={scrollToTestimonials}
          data-testid="read-reviews-link"
          className="text-sm font-medium text-terracotta underline underline-offset-2 hover:text-terracotta-hover transition-colors"
        >
          {t("home.readReviews")}
        </button>
      </div>
    </section>
  );
}
